import {
	Component,
	OnDestroy,
	inject,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { provideDayjsDateAdapter } from '@lmc-app/shared/adapters/dayjs-date-adapter';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { Subject, takeUntil } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Loading } from '../../../../applications/loading';

import { Dayjs } from 'dayjs';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatHint,
	MatSuffix,
} from '@angular/material/input';
import { CustomTableComponent } from '../../../../custom-table/custom-table.component';

export const MY_FORMATS = {
	parse: {
		dateInput: 'MM/YYYY',
	},
	display: {
		dateInput: 'MM/YYYY',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};

@Component({
	selector: 'app-funding-exception',
	templateUrl: './funding-exception.component.html',
	styleUrls: ['./funding-exception.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [provideDayjsDateAdapter(MY_FORMATS)],
	imports: [
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		MatHint,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		CustomTableComponent,
	],
})
export class FundingExceptionComponent implements OnDestroy {
	private readonly _onDestroy = new Subject<void>();
	private userData: User | null = null;
	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);
	protected fundingExceptions: any;

	inputDateForm = new FormGroup({
		input_date: new FormControl(null),
		target: new FormControl(null),
		num_business_day: new FormControl(null),
	});
	protected columnDefs: ColDef[] = [
		{ headerName: 'Funded Date', field: 'funded_date' },
		{ headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
		{ headerName: 'Account Number', field: 'account_number' },
		{
			headerName: 'Funded Amount',
			field: 'funded_amount',
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.funded_amount,
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Exception',
			field: 'exception',
			filter: 'agTextColumnFilter',
		},
		{
			headerName: 'Status',
			field: 'exception_status',
			filter: 'agTextColumnFilter',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['Pending', 'Completed'],
			},
		},
	];

	constructor(
		private readonly currencyFormatter: CurrencyPipe,
		private loanAccountService: LoanAccountService,
		private store: Store<State>
	) {
		this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				this.userData = user;
			});
		this.inputDateForm.controls.input_date.setValue(new Date());
		const inputDateValue = this.inputDateForm.controls.input_date.value;
		this.getFundingExceptions(
			inputDateValue.getFullYear(),
			inputDateValue.getMonth() + 1
		);
	}

	getFundingExceptions(year, month) {
		this.loanAccountService.getExceptions(year, month).subscribe((res: any) => {
			this.fundingExceptions = res;
		});
	}

	setMonthAndYear(
		normalizedMonthAndYear: Dayjs,
		datepicker: MatDatepicker<Dayjs>
	) {
		const year = normalizedMonthAndYear.year();
		const month = normalizedMonthAndYear.month() + 1;
		const inputDate = `${year}-${month.toString().padStart(2, '0')}-02`;
		this.inputDateForm.controls.input_date.setValue(new Date(inputDate));
		this.getFundingExceptions(year, month);
		datepicker.close();
	}

	onCellValueChanged(e) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving' },
		});
		const inputDateValue = this.inputDateForm.controls.input_date.value;
		this.loanAccountService.updateLoanAccountException(e.data).subscribe({
			next: () => {
				this.getFundingExceptions(
					inputDateValue.getFullYear(),
					inputDateValue.getMonth() + 1
				);
				this._snackBar.open(
					`Loan account for ${e.data.name} is updated.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
			},
			error: (error) => {
				this.getFundingExceptions(
					inputDateValue.getFullYear(),
					inputDateValue.getMonth() + 1
				);
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error &&
						error.error.message &&
						(error.error.message.name || error.error.message)) ||
						'Error updating loan account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
